import localizedFormat from "dayjs/plugin/localizedFormat";
import dayjs from "dayjs";
import { TimeUnit } from "./order/typesDefinition";

dayjs.extend(localizedFormat);

export function formatPrice(price: number): string {
  if (isNaN(price)) return "-";
  const priceInEuros = price / 100;
  return priceInEuros.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
}
export function parsePriceToCents(val: string): number {
  const normalized = val.replace(/[^\d,.-]/g, "").replace(",", ".");
  return Math.round(parseFloat(normalized) * 100);
}

export function formatFloat(value: number): string {
  if (isNaN(value)) return "-";
  return value.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function formatFullDate(date: Date): string {
  return dayjs(date).format("LLLL");
}

export const formatPercentage = (decimalPercentage: number): string => {
  if (isNaN(decimalPercentage)) {
    return "-";
  }
  return Number(decimalPercentage).toLocaleString("fr", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export function stringCompareWithNullsLast(
  str1: string | undefined,
  str2: string | undefined,
): number {
  if (!str1 && !str2) return 0;
  if (!str2) return -1;
  if (!str1) return 1;
  return str1.localeCompare(str2);
}

export function isInt(n: number): boolean {
  return n % 1 === 0;
}

export function quarterStepsValidator(value: number): boolean {
  return isInt(value * 4);
}

export const computeOrderLineSubtotal = (
  quantity: number,
  hourlyRate: number,
): number => {
  return (quantity * hourlyRate) / 60;
};

/**
 * Converts hourly rate to display rate based on time unit.
 * For day units, multiplies by 8 to show daily rate.
 * @param hourlyRate - Rate in cents per hour
 * @param unit - Time unit (hour or day)
 * @returns Display rate (hourly rate, or daily rate for day unit)
 */
export const computeDisplayRate = (
  hourlyRate: number,
  unit: TimeUnit,
): number => {
  if (unit === TimeUnit.DAY) {
    return hourlyRate * 8;
  }
  return hourlyRate;
};

export function shadeColor(color: string, percent: number) {
  let r = parseInt(color.substring(1, 3), 16);
  let g = parseInt(color.substring(3, 5), 16);
  let b = parseInt(color.substring(5, 7), 16);

  r = (r * (100 + percent)) / 100;
  g = (g * (100 + percent)) / 100;
  b = (b * (100 + percent)) / 100;

  r = r < 255 ? r : 255;
  g = g < 255 ? g : 255;
  b = b < 255 ? b : 255;

  r = Math.round(r);
  g = Math.round(g);
  b = Math.round(b);

  const rr = r.toString(16).length == 1 ? "0" + r.toString(16) : r.toString(16);
  const gg = g.toString(16).length == 1 ? "0" + g.toString(16) : g.toString(16);
  const bb = b.toString(16).length == 1 ? "0" + b.toString(16) : b.toString(16);

  return `#${rr}${gg}${bb}`;
}

/**
 * Formats minutes as hh:mm string for display
 * @param minutes - duration in minutes
 * @returns formatted string in hh:mm format (e.g., "08:30" for 510 minutes)
 */
export function formatMinutesAsTime(minutes: number): string {
  const safeMinutes = Math.max(Math.round(minutes), 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(remainingMinutes).padStart(
    2,
    "0",
  )}`;
}

/**
 * Parses hh:mm string to minutes for form input
 * @param timeString - time in hh:mm format (e.g., "08:30")
 * @returns duration in minutes
 */
export function parseTimeToMinutes(timeString: string): number {
  if (!timeString) {
    return 0;
  }

  const parts = timeString.trim().split(":");
  if (parts.length !== 2) {
    return 0;
  }

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }

  if (hours < 0 || minutes < 0 || minutes >= 60) {
    return 0;
  }

  return hours * 60 + minutes;
}

/**
 * Formats hours as hh:mm string for display (for planned time fields like hoursPerDay)
 * @param hours - duration in hours (e.g., 8 for 8 hours, 7.5 for 7.5 hours)
 * @returns formatted string in hh:mm format (e.g., "08:00" for 8 hours, "07:30" for 7.5 hours)
 */
export function formatHoursAsTime(hours: number): string {
  const safeHours = Math.max(hours, 0);
  const totalMinutes = Math.round(safeHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Parses hh:mm string to hours for form input (for planned time fields)
 * @param timeString - time in hh:mm format (e.g., "08:00")
 * @returns duration in hours (as number)
 */
export function parseTimeToHours(timeString: string): number {
  const minutes = parseTimeToMinutes(timeString);
  return Math.round(minutes / 60);
}
